// Client auth context — boots from /api/me, exposes { user, refresh, logout }.

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const AuthContext = createContext({
  user: null,
  status: 'idle', // idle | loading | ready | error
  refresh: async () => {},
  signIn: () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('idle');

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/me', { credentials: 'same-origin' });
      const body = await res.json();
      if (body?.ok) {
        setUser(body.data?.user ?? null);
        setStatus('ready');
      } else {
        setUser(null);
        setStatus('ready');
      }
    } catch {
      setUser(null);
      setStatus('error');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signIn = useCallback(() => {
    const here = window.location.pathname + window.location.hash;
    window.location.href = `/api/auth/google/start?return_to=${encodeURIComponent(here)}`;
  }, []);

  const signOut = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    setUser(null);
  }, []);

  const value = { user, status, refresh, signIn, signOut };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
